/**
 * Time calculation utilities for itinerary waypoints
 * Supports duration calculations and travel time calculations for future validation
 */

/**
 * Format time from HH:MM or HH:MM:SS string to military format (HHMM)
 * Example: "10:00" -> "1000", "14:15" -> "1415", "13:00:00" -> "1300"
 */
export function formatTimeToMilitary(timeString: string | null | undefined): string | null {
  if (!timeString) return null
  
  // If already in military format (4 digits), return as-is
  if (/^\d{4}$/.test(timeString)) {
    return timeString
  }
  
  // Parse HH:MM or HH:MM:SS format (database TIME type returns HH:MM:SS)
  const match = timeString.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (!match) return null
  
  const hours = parseInt(match[1], 10)
  const minutes = parseInt(match[2], 10)
  
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null
  }
  
  return `${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}`
}

/**
 * Parse military time format (HHMM) to HH:MM
 * Example: "1000" -> "10:00", "1415" -> "14:15"
 */
export function parseMilitaryTime(militaryTime: string | null | undefined): string | null {
  if (!militaryTime) return null
  
  // If already in HH:MM format, return as-is
  if (/^\d{1,2}:\d{2}$/.test(militaryTime)) {
    return militaryTime
  }
  
  // Parse HHMM format (4 digits)
  const match = militaryTime.match(/^(\d{2})(\d{2})$/)
  if (!match) return null
  
  const hours = parseInt(match[1], 10)
  const minutes = parseInt(match[2], 10)
  
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null
  }
  
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

/**
 * Calculate duration in minutes between two times
 * @param startTime Start time in HH:MM format
 * @param endTime End time in HH:MM format
 * @returns Duration in minutes, or null if invalid
 */
export function calculateDurationMinutes(
  startTime: string | null | undefined,
  endTime: string | null | undefined
): number | null {
  if (!startTime || !endTime) return null
  
  const start = parseTimeToMinutes(startTime)
  const end = parseTimeToMinutes(endTime)
  
  if (start === null || end === null) return null
  
  // Handle case where end time is next day (e.g., 23:00 to 01:00)
  if (end < start) {
    return (24 * 60 - start) + end
  }
  
  return end - start
}

/**
 * Parse time string (HH:MM or HHMM) to minutes since midnight
 */
function parseTimeToMinutes(timeString: string): number | null {
  const hhmm = parseMilitaryTime(timeString)
  if (!hhmm) return null
  
  const [hours, minutes] = hhmm.split(':').map(Number)
  return hours * 60 + minutes
}

/**
 * Format duration in minutes to human-readable string
 * Example: 150 -> "2h 30m", 45 -> "45m"
 */
export function formatDuration(durationMinutes: number | null): string | null {
  if (durationMinutes === null || durationMinutes < 0) return null
  
  if (durationMinutes < 60) {
    return `${durationMinutes}m`
  }
  
  const hours = Math.floor(durationMinutes / 60)
  const minutes = durationMinutes % 60
  
  if (minutes === 0) {
    return `${hours}h`
  }
  
  return `${hours}h ${minutes}m`
}

/**
 * Calculate travel time in minutes between two waypoints
 * Uses distance calculation and average speed
 * @param distanceMeters Distance in meters
 * @param averageSpeedKmh Average speed in km/h (default: 50)
 * @returns Travel time in minutes
 */
export function calculateTravelTimeMinutes(
  distanceMeters: number,
  averageSpeedKmh: number = 50
): number {
  if (distanceMeters <= 0 || averageSpeedKmh <= 0) return 0
  
  // Convert distance to km
  const distanceKm = distanceMeters / 1000
  
  // Calculate time in hours
  const timeHours = distanceKm / averageSpeedKmh
  
  // Convert to minutes and round
  return Math.round(timeHours * 60)
}

/**
 * Format time range for display
 * Example: startTime="10:00", endTime="14:00" -> "1000-1400"
 */
export function formatTimeRange(
  startTime: string | null | undefined,
  endTime: string | null | undefined
): string | null {
  if (!startTime && !endTime) return null
  
  const start = formatTimeToMilitary(startTime)
  const end = formatTimeToMilitary(endTime)
  
  if (start && end) {
    return `${start}-${end}`
  }
  
  if (start) return start
  if (end) return end
  
  return null
}

/**
 * Format time in 12-hour format with AM/PM
 * Example: "10:00" -> "10:00 AM", "14:30" -> "2:30 PM", "00:00" -> "12:00 AM"
 * Handles HH:MM, HH:MM:SS, and HHMM formats
 */
export function formatTime12Hour(timeString: string | null | undefined): string | null {
  if (!timeString) return null
  
  let hours: number
  let minutes: number
  
  // Handle HH:MM:SS format (database returns this)
  if (/^\d{1,2}:\d{2}:\d{2}$/.test(timeString)) {
    const parts = timeString.split(':')
    hours = parseInt(parts[0], 10)
    minutes = parseInt(parts[1], 10)
  }
  // Handle HH:MM format
  else if (/^\d{1,2}:\d{2}$/.test(timeString)) {
    const parts = timeString.split(':')
    hours = parseInt(parts[0], 10)
    minutes = parseInt(parts[1], 10)
  }
  // Handle HHMM format (4 digits)
  else if (/^\d{4}$/.test(timeString)) {
    hours = parseInt(timeString.substring(0, 2), 10)
    minutes = parseInt(timeString.substring(2, 4), 10)
  }
  else {
    return null
  }
  
  // Validate
  if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null
  }
  
  // Convert to 12-hour format
  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
  const displayMinutes = String(minutes).padStart(2, '0')
  
  return `${displayHours}:${displayMinutes} ${period}`
}

/**
 * Format time for display - uses 12-hour format by default
 * Example: "10:00" -> "10:00 AM", "14:30" -> "2:30 PM"
 */
export function formatTimeForDisplay(timeString: string | null | undefined): string | null {
  return formatTime12Hour(timeString)
}

/**
 * Check if arrival time is on a different day than departure time
 * This happens when end time is earlier than start time (e.g., 23:00 to 01:00)
 * @param startTime Start time in HH:MM format
 * @param endTime End time in HH:MM format
 * @returns true if arrival is on the next day, false otherwise
 */
export function isArrivalNextDay(
  startTime: string | null | undefined,
  endTime: string | null | undefined
): boolean {
  if (!startTime || !endTime) return false
  
  const start = parseTimeToMinutes(startTime)
  const end = parseTimeToMinutes(endTime)
  
  if (start === null || end === null) return false
  
  // If end time is earlier than start time, it's next day
  return end < start
}

/**
 * Format duration in airline-style format
 * Example: 150 -> "2 hours 30 minutes", 45 -> "45 minutes", 60 -> "1 hour"
 * @param durationMinutes Duration in minutes
 * @returns Formatted duration string
 */
export function formatDurationAirlineStyle(durationMinutes: number | null): string | null {
  if (durationMinutes === null || durationMinutes < 0) return null
  
  if (durationMinutes < 60) {
    return `${durationMinutes} ${durationMinutes === 1 ? 'minute' : 'minutes'}`
  }
  
  const hours = Math.floor(durationMinutes / 60)
  const minutes = durationMinutes % 60
  
  const hoursText = hours === 1 ? 'hour' : 'hours'
  const minutesText = minutes === 1 ? 'minute' : 'minutes'
  
  if (minutes === 0) {
    return `${hours} ${hoursText}`
  }
  
  return `${hours} ${hoursText} ${minutes} ${minutesText}`
}